import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { CartItem, User, Language } from '../types'
import { forgetDraft } from '../lib/specsDraft'
import { clearQueryCache } from '../lib/queryClient'

interface AuthState {
  user: User | null
  token: string | null
  setAuth: (user: User, token: string) => void
  logout: () => void
}

interface LanguageState {
  language: Language
  setLanguage: (language: Language) => void
}

interface CartState {
  items: CartItem[]
  addItem: (item: CartItem) => void
  removeItem: (equipmentId: number) => void
  updateQuantity: (equipmentId: number, quantity: number) => void
  clearCart: () => void
  getTotalItems: () => number
}

// Auth store
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      setAuth: (user, token) => {
        set({ user, token })
      },
      logout: () => {
        set({ user: null, token: null })
        localStorage.removeItem('broxel-auth')
        // Clear cart when user logs out
        useCartStore.getState().clearCart()
        // ...and the guest BroxSpecs draft this browser remembers. It is bound
        // to the browser, not the person, and claiming it transfers ownership
        // permanently - so on a shared machine the next person to sign in must
        // not be offered it. See forgetDraft's own comment. This covers the
        // 401 interceptor in lib/api.ts too, which logs out through here.
        forgetDraft()
        // ...and every cached API response. Same sentence, one step further:
        // `logout()` leaves with navigate('/') and no reload, and staleTime is
        // 0, so without this the next person to sign in on this browser gets
        // ONE PAINT of the previous account's data before the refetch lands -
        // their orders, their enquiries, and worst of all their AI
        // conversation titles. The keys are also scoped per identity now (see
        // lib/queryKeys.ts); this is the backstop that does not depend on
        // every one of them having been scoped correctly.
        clearQueryCache()
      },
    }),
    {
      name: 'broxel-auth',
    }
  )
)

// Language store
export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      language: 'en',
      setLanguage: (language) => set({ language }),
    }),
    {
      name: 'broxel-language',
    }
  )
)

// Cart store
export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (newItem) =>
        set((state) => {
          const existingItemIndex = state.items.findIndex(
            (item) => item.equipment.id === newItem.equipment.id
          )
          
          if (existingItemIndex >= 0) {
            // Update existing item quantity
            const updatedItems = [...state.items]
            updatedItems[existingItemIndex].quantity += newItem.quantity
            return { items: updatedItems }
          } else {
            // Add new item
            return { items: [...state.items, newItem] }
          }
        }),
      removeItem: (equipmentId) =>
        set((state) => ({
          items: state.items.filter((item) => item.equipment.id !== equipmentId),
        })),
      updateQuantity: (equipmentId, quantity) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.equipment.id === equipmentId
              ? { ...item, quantity: Math.max(0, quantity) }
              : item
          ).filter((item) => item.quantity > 0),
        })),
      clearCart: () => set({ items: [] }),
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0)
      },
    }),
    {
      name: 'broxel-cart',
    }
  )
)
